import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleLocationClick = () => {
    scrollToSection('contatti');
    // Open Google Maps after scrolling
    setTimeout(() => {
      window.open('https://www.google.com/maps/search/?api=1&query=Via+Roma+129,+66010+Torrevecchia+Teatina+CH', '_blank');
    }, 500);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer">
            <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center">
              <i className="ri-building-2-fill text-2xl sm:text-3xl text-amber-700"></i>
            </div>
            <span className="text-lg sm:text-xl font-bold text-gray-900">Bar Vecchiatorre</span>
          </div>
          
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <button onClick={() => scrollToSection('bar')} className="text-gray-700 hover:text-amber-700 transition-colors whitespace-nowrap cursor-pointer">Bar</button>
            <button onClick={() => scrollToSection('tabaccheria')} className="text-gray-700 hover:text-amber-700 transition-colors whitespace-nowrap cursor-pointer">Tabaccheria</button>
            <button onClick={() => scrollToSection('scommesse')} className="text-gray-700 hover:text-amber-700 transition-colors whitespace-nowrap cursor-pointer">Scommesse</button>
            <button onClick={() => scrollToSection('lottomatica')} className="text-gray-700 hover:text-amber-700 transition-colors whitespace-nowrap cursor-pointer">Lottomatica</button>
            <button onClick={() => scrollToSection('contatti')} className="text-gray-700 hover:text-amber-700 transition-colors whitespace-nowrap cursor-pointer">Contatti</button>
          </div>
          
          <button onClick={handleLocationClick} className="hidden md:flex items-center gap-2 bg-amber-700 text-white px-4 lg:px-6 py-2.5 rounded-full hover:bg-amber-800 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-map-pin-line text-lg"></i>
            <span>Dove Siamo</span>
          </button>
          
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-gray-900 text-2xl cursor-pointer">
            <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-4 py-4">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('bar')} className="text-gray-700 hover:text-amber-700 transition-colors text-left cursor-pointer">Bar</button>
              <button onClick={() => scrollToSection('tabaccheria')} className="text-gray-700 hover:text-amber-700 transition-colors text-left cursor-pointer">Tabaccheria</button>
              <button onClick={() => scrollToSection('scommesse')} className="text-gray-700 hover:text-amber-700 transition-colors text-left cursor-pointer">Scommesse</button>
              <button onClick={() => scrollToSection('lottomatica')} className="text-gray-700 hover:text-amber-700 transition-colors text-left cursor-pointer">Lottomatica</button>
              <button onClick={() => scrollToSection('contatti')} className="text-gray-700 hover:text-amber-700 transition-colors text-left cursor-pointer">Contatti</button>
              <button onClick={handleLocationClick} className="flex items-center gap-2 bg-amber-700 text-white px-6 py-2.5 rounded-full hover:bg-amber-800 transition-colors whitespace-nowrap cursor-pointer justify-center">
                <i className="ri-map-pin-line text-lg"></i>
                <span>Dove Siamo</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center pt-20 px-4 sm:px-6">
        <div className="absolute inset-0 mx-4 sm:mx-6 rounded-2xl sm:rounded-3xl overflow-hidden">
          <img 
            alt="Bar Vecchiatorre" 
            className="w-full h-full object-cover object-top" 
            src="https://static.readdy.ai/image/c5a3817801857dccd49092e485c7934c/8719b2cbb06fd367ebcad88956d4d308.jpeg"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
          <div className="inline-block mb-4 sm:mb-6 px-4 sm:px-6 py-2 border-2 border-white/40 rounded-full backdrop-blur-sm">
            <span className="text-white text-xs sm:text-sm font-medium">Dal 1974 a Torrevecchia Teatina</span>
          </div>
          
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Bar<br />Vecchiatorre
          </h1>
          
          <p className="text-white/90 text-base sm:text-lg md:text-xl font-light mb-8 sm:mb-12">
            Bar • Tabaccheria • Ricevitoria • Sisal Matchpoint
          </p>
          
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 justify-center">
            <button onClick={() => scrollToSection('bar')} className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-gray-900 rounded-full font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Scopri i Servizi
            </button>
            <button onClick={handleLocationClick} className="px-6 sm:px-8 py-3 sm:py-4 bg-transparent border-2 border-white text-white rounded-full font-medium hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer">
              Come Arrivare
            </button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#F5F1E8]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <p className="text-xs sm:text-sm text-gray-600 mb-6 sm:mb-8">/ Chi Siamo</p>
            
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
              <div>
                <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-relaxed mb-6 sm:mb-8">
                  Nel cuore di Torrevecchia Teatina, in Via Roma 129, offriamo un punto di riferimento completo per la comunità. Dal caffè mattutino alle scommesse sportive, dalla tabaccheria ai servizi Lottomatica.
                </p>
                
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                  <div className="w-2 h-2 bg-amber-700 rounded-full"></div>
                  <span>Servizi completi dal 1974</span>
                </div>
              </div>
              
              <div className="flex flex-col gap-6 sm:gap-8">
                <div className="flex items-center gap-4 sm:gap-6">
                  <div className="flex gap-2 sm:gap-3">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-amber-100 flex items-center justify-center">
                      <i className="ri-cup-line text-xl sm:text-2xl text-amber-800"></i>
                    </div>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-amber-100 flex items-center justify-center">
                      <i className="ri-fire-line text-xl sm:text-2xl text-amber-800"></i>
                    </div>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-amber-100 flex items-center justify-center">
                      <i className="ri-ticket-line text-xl sm:text-2xl text-amber-800"></i>
                    </div>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-amber-100 flex items-center justify-center">
                      <i className="ri-money-euro-circle-line text-xl sm:text-2xl text-amber-800"></i>
                    </div>
                  </div>
                </div>
                
                <p className="text-base sm:text-lg text-gray-900 font-light uppercase tracking-wide relative inline-block">
                  Oltre <strong className="font-bold text-amber-700 relative">50 anni<span className="absolute bottom-0 left-0 w-full h-1 bg-amber-700"></span></strong> di esperienza al servizio della comunità locale
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="bar" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 sm:mb-16">
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">/ I Nostri Servizi</p>
            
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 sm:gap-8">
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-tight">
                Tutto quello che<br />cerchi in un unico posto
              </h2>
              
              <p className="text-base sm:text-lg text-gray-600 max-w-md">
                Dal 1974 offriamo servizi completi per soddisfare ogni esigenza quotidiana della nostra comunità
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {/* Bar & Caffetteria */}
            <div className="md:col-span-2 bg-[#2C4A3E] rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 relative overflow-hidden min-h-[400px] sm:min-h-[500px] flex flex-col justify-between">
              <div className="relative z-10">
                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">Bar & Caffetteria</h3>
                <p className="text-white/80 text-base sm:text-lg font-light mb-6 sm:mb-8">
                  Colazioni, aperitivi e pausa pranzo
                </p>
              </div>
              
              <div className="absolute inset-0 opacity-20">
                <img 
                  alt="Bar" 
                  className="w-full h-full object-cover" 
                  src="https://public.readdy.ai/ai/img_res/a437c178cc30636c877d095b15b117d1.jpg"
                />
              </div>
              
              <button className="relative z-10 inline-flex items-center gap-2 sm:gap-3 bg-white text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-full font-medium hover:bg-gray-100 transition-colors self-start whitespace-nowrap cursor-pointer">
                <span>Scopri il Menu</span>
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-900 rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white"></i>
                </div>
              </button>
            </div>
            
            {/* Tabaccheria */}
            <div id="tabaccheria" className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 relative overflow-hidden min-h-[400px] sm:min-h-[500px] flex flex-col">
              <div className="inline-flex items-center gap-2 bg-emerald-500 text-white px-3 sm:px-4 py-2 rounded-full self-start mb-4 sm:mb-6">
                <i className="ri-fire-line"></i>
                <span className="text-xs sm:text-sm font-medium">Tabaccheria</span>
              </div>
              
              <div className="flex-1 mb-4 sm:mb-6 rounded-xl sm:rounded-2xl overflow-hidden">
                <img 
                  alt="Tabaccheria" 
                  className="w-full h-full object-cover" 
                  src="https://static.readdy.ai/image/c5a3817801857dccd49092e485c7934c/d96de4143a862f62f90129806aea2a08.jpeg"
                />
              </div>
              
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Tabacchi e Accessori</h3>
                <p className="text-sm sm:text-base text-gray-600">Sigarette, sigari, accessori fumatori</p>
              </div>
            </div>
            
            {/* Scommesse */}
            <div id="scommesse" className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 relative overflow-hidden min-h-[400px] sm:min-h-[500px] flex flex-col">
              <div className="inline-flex items-center gap-2 bg-orange-500 text-white px-3 sm:px-4 py-2 rounded-full self-start mb-4 sm:mb-6">
                <i className="ri-football-line"></i>
                <span className="text-xs sm:text-sm font-medium">Scommesse</span>
              </div>
              
              <div className="flex-1 mb-4 sm:mb-6 rounded-xl sm:rounded-2xl overflow-hidden">
                <img 
                  alt="Scommesse" 
                  className="w-full h-full object-cover" 
                  src="https://public.readdy.ai/ai/img_res/5e3fa62a8f2e888b7b94fd00200207d5.jpg"
                />
              </div>
              
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Sisal Matchpoint</h3>
                <p className="text-sm sm:text-base text-gray-600">Scommesse sportive e ippiche</p>
              </div>
            </div>
            
            {/* Complete Service */}
            <div className="md:col-span-2 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 relative overflow-hidden min-h-[350px] sm:min-h-[400px] flex items-center">
              <div className="grid md:grid-cols-2 gap-6 sm:gap-8 w-full">
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">Servizio Completo</h3>
                  <p className="text-gray-700 text-base sm:text-lg mb-4 sm:mb-6">
                    Tutto ciò di cui hai bisogno in un unico punto: dalla colazione al caffè, dalle ricariche telefoniche alle scommesse sportive.
                  </p>
                  
                  <div className="flex flex-wrap gap-2 sm:gap-3">
                    <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white rounded-full text-xs sm:text-sm font-medium text-gray-700">Caffetteria</span>
                    <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white rounded-full text-xs sm:text-sm font-medium text-gray-700">Tabacchi</span>
                    <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white rounded-full text-xs sm:text-sm font-medium text-gray-700">Scommesse</span>
                    <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white rounded-full text-xs sm:text-sm font-medium text-gray-700">Lotto</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-center">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center">
                      <div className="text-3xl sm:text-4xl font-bold text-amber-700 mb-1 sm:mb-2">50+</div>
                      <div className="text-xs sm:text-sm text-gray-600">Anni di Esperienza</div>
                    </div>
                    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center">
                      <div className="text-3xl sm:text-4xl font-bold text-amber-700 mb-1 sm:mb-2">6</div>
                      <div className="text-xs sm:text-sm text-gray-600">Servizi Offerti</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lottomatica Section */}
      <section id="lottomatica" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#F8F6F0]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 sm:mb-16">
            <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">/ Servizi Ricevitoria</p>
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 leading-tight">
              Lottomatica e<br />Servizi Digitali
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-[#FAFAF8] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-ticket-2-line text-3xl sm:text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">SuperEnalotto & Lotto</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Gioca alle estrazioni, 10eLotto, MillionDay. Verifica le tue vincite e ritira i premi direttamente in sede.
              </p>
            </div>
            
            <div className="bg-[#FAFAF8] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-smartphone-line text-3xl sm:text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Ricariche Telefoniche</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Tutti gli operatori, ricariche immediate. TIM, Vodafone, WindTre, Iliad e tutti i principali gestori.
              </p>
            </div>
            
            <div className="bg-[#FAFAF8] rounded-xl sm:rounded-2xl p-6 sm:p-8 md:p-10 hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-file-list-3-line text-3xl sm:text-4xl text-gray-900"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Pagamenti & Bollettini</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Bollettini postali, F24, MAV, RAV. Pagamento utenze e servizi in modo rapido e sicuro.
              </p>
            </div>
          </div>
          
          <div className="mt-12 sm:mt-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl sm:rounded-3xl p-8 sm:p-10 md:p-12 text-center">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">Punto Lottomatica Autorizzato</h3>
            <p className="text-white/90 text-base sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
              Siamo un punto vendita autorizzato Lottomatica. Tutti i servizi di gioco e pagamento in totale sicurezza e affidabilità.
            </p>
            
            <div className="flex flex-wrap gap-3 sm:gap-4 justify-center">
              <div className="bg-white/20 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full text-white font-medium text-sm sm:text-base">
                <i className="ri-shield-check-line mr-2"></i>Punto Autorizzato
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full text-white font-medium text-sm sm:text-base">
                <i className="ri-time-line mr-2"></i>Servizio Rapido
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-4 sm:px-6 py-2 sm:py-3 rounded-full text-white font-medium text-sm sm:text-base">
                <i className="ri-customer-service-2-line mr-2"></i>Assistenza Dedicata
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-[#FDFCFA]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl text-center text-gray-800 mb-12 sm:mb-16 font-serif italic">
            (Cosa dicono i nostri clienti)
          </h2>
          
          <div className="relative">
            <div className="mb-6 sm:mb-8">
              <div className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 sm:px-4 py-2 rounded-full mb-6 sm:mb-8">
                <i className="ri-star-fill text-yellow-400"></i>
                <span className="font-medium text-sm sm:text-base">4.8/5.0</span>
              </div>
              
              <blockquote className="text-xl sm:text-2xl md:text-3xl text-gray-800 leading-relaxed mb-6 sm:mb-8 font-serif">
                "Il mio bar di fiducia da anni. Personale cordiale, ottimo caffè e tutti i servizi che servono. Comodo anche per le scommesse del weekend!"
              </blockquote>
              
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-amber-100 rounded-full flex items-center justify-center">
                  <i className="ri-user-line text-xl sm:text-2xl text-amber-700"></i>
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-sm sm:text-base">Marco R.</div>
                  <div className="text-xs sm:text-sm text-gray-600">Cliente abituale</div>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 sm:gap-3 justify-end">
              <button className="w-10 h-10 sm:w-12 sm:h-12 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer">
                <i className="ri-arrow-left-line text-gray-900"></i>
              </button>
              <button className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-900 rounded-lg flex items-center justify-center hover:bg-gray-800 transition-colors cursor-pointer">
                <i className="ri-arrow-right-line text-white"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Gaming Section */}
      <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 sm:p-10 md:p-12 lg:p-16 flex flex-col justify-center order-2 md:order-1">
                <div className="inline-block mb-4 sm:mb-6 px-3 sm:px-4 py-2 bg-amber-100 rounded-full w-fit">
                  <span className="text-amber-800 text-xs sm:text-sm font-semibold">SCOMMESSE SPORTIVE</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                  Gioca con noi
                </h2>
                
                <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                  Sisal Matchpoint e Lotto sempre disponibili. Vieni a provare l'emozione delle scommesse sportive in un ambiente sicuro e professionale.
                </p>
                
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <div className="bg-amber-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                    <span className="text-amber-800 text-xs sm:text-sm font-semibold">Sisal Matchpoint</span>
                  </div>
                  <div className="bg-emerald-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                    <span className="text-emerald-800 text-xs sm:text-sm font-semibold">Lotto</span>
                  </div>
                  <div className="bg-purple-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                    <span className="text-purple-800 text-xs sm:text-sm font-semibold">SuperEnalotto</span>
                  </div>
                  <div className="bg-blue-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                    <span className="text-blue-800 text-xs sm:text-sm font-semibold">MillionDay</span>
                  </div>
                  <div className="bg-rose-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                    <span className="text-rose-800 text-xs sm:text-sm font-semibold">10eLotto</span>
                  </div>
                  <div className="bg-teal-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                    <span className="text-teal-800 text-xs sm:text-sm font-semibold">Ricarica Conto Gioco</span>
                  </div>
                </div>
              </div>
              
              <div className="relative h-[300px] sm:h-[400px] md:h-auto order-1 md:order-2">
                <img 
                  alt="Scommesse Sportive" 
                  className="w-full h-full object-cover object-top" 
                  src="https://public.readdy.ai/ai/img_res/0593915aa404ab26a85cf5828e0d2b35.jpg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contatti" className="bg-[#1A3329] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 md:gap-12 mb-12 sm:mb-16">
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center">
                  <i className="ri-building-2-fill text-2xl sm:text-3xl text-amber-400"></i>
                </div>
                <span className="text-lg sm:text-xl font-bold text-amber-100">Bar Vecchiatorre</span>
              </div>
              
              <div className="text-gray-300 text-xs sm:text-sm space-y-1">
                <p>Via Roma 129</p>
                <p>66010 Torrevecchia Teatina (CH)</p>
                <p>P.IVA:</p>
              </div>
            </div>
            
            <div>
              <h4 className="text-xs uppercase text-gray-400 mb-3 sm:mb-4 tracking-wider">NEWSLETTER</h4>
              <form data-readdy-form={true} id="newsletter-footer">
                <div className="relative">
                  <input 
                    placeholder="La tua email" 
                    className="w-full bg-transparent border-b border-gray-500 pb-2 text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors text-sm sm:text-base" 
                    required 
                    type="email" 
                    name="email"
                  />
                  <button type="submit" className="absolute right-0 bottom-2 text-gray-400 hover:text-amber-400 transition-colors cursor-pointer">
                    <i className="ri-arrow-right-line"></i>
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">Iscriviti per offerte esclusive</p>
              </form>
            </div>
            
            <div>
              <h4 className="text-xs uppercase text-gray-400 mb-3 sm:mb-4 tracking-wider">ORARI</h4>
              <div className="text-gray-300 text-xs sm:text-sm space-y-2">
                <p>lunedì 06:30–13</p>
                <p>mar-Sab: 06:30–13; 15:30–20:30</p>
                <p>domenica 07–13, 16–20:30</p>
              </div>
            </div>
            
            <div>
              <h4 className="text-xs uppercase text-gray-400 mb-3 sm:mb-4 tracking-wider">CONTATTI</h4>
              <div className="text-gray-300 text-xs sm:text-sm space-y-2">
                <a href="tel:3478777965" className="block hover:text-amber-400 transition-colors cursor-pointer">
                  Tel: 347 877 7965
                </a>
                <a href="mailto:info.vecchiatorre@tiscali.it" className="block hover:text-amber-400 transition-colors cursor-pointer">
                  Email: info.vecchiatorre@tiscali.it
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="block hover:text-amber-400 transition-colors cursor-pointer">
                  Facebook
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 sm:pt-12">
            <div className="text-center">
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-amber-100/20 tracking-tight">
                BAR VECCHIATORRE
              </h2>
            </div>
          </div>
          
          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-700 text-center text-xs sm:text-sm text-gray-400">
            <p>
              © 2024 Bar Vecchiatorre. Tutti i diritti riservati. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-amber-400 transition-colors cursor-pointer">Website Builder</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
